import { useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, DialogActions, DialogContent, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { AudioFile, CloudUpload, Delete, Download, Edit, PlayArrow, Videocam } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { creativesService, Creative, CreativeType } from '../../services/creatives.service';
import { useAuthStore } from '../../stores/auth.store';
import { useServerPagination } from '../../hooks/useServerPagination';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useDebounced } from '../../hooks/useDebounced';
import EntityGridToolbar from '../../components/EntityGridToolbar';
import TooltipIconButton from '../../components/TooltipIconButton';
import DraggableDialog from '../../components/DraggableDialog';
import ConfirmDialog from '../../components/ConfirmDialog';
import CreativeViewerDialog from './CreativeViewerDialog';
import UploadCreativeDialog from './UploadCreativeDialog';
import { formatDuration, formatFileSize, SOURCE_LABEL } from './creative-format';

const TYPES: Array<CreativeType | 'all'> = ['all', 'image', 'video', 'audio'];

function Thumb({ creative, onOpen }: { creative: Creative; onOpen: () => void }) {
  const src = creative.thumbnail_url || (creative.type === 'image' ? creative.signed_url : undefined);
  const box = { width: 48, height: 48, borderRadius: 1, cursor: 'pointer', position: 'relative' as const, overflow: 'hidden', flexShrink: 0 };
  return (
    <Box
      role="button" aria-label={`View ${creative.name}`} tabIndex={0}
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      sx={{ ...box, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { opacity: 0.85 } }}
    >
      {src ? (
        <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : creative.type === 'audio' ? (
        <AudioFile sx={{ color: 'text.secondary' }} />
      ) : (
        <Videocam sx={{ color: 'text.secondary' }} />
      )}
      {creative.type === 'video' && src && (
        <PlayArrow sx={{ position: 'absolute', color: 'common.white', fontSize: 22, filter: 'drop-shadow(0 0 2px rgba(0,0,0,.8))' }} />
      )}
    </Box>
  );
}

export default function CreativesPage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const failed = (what: string) => (e: unknown) => {
    const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string };
    enqueueSnackbar(`${what}: ${err.response?.data?.error?.message || err.message || 'failed'}`, { variant: 'error' });
  };
  const workspaces = useAuthStore((s) => s.user?.workspaces ?? []);
  const workspaceName = new Map(workspaces.map((w) => [w.id, w.name]));

  const [search, setSearch] = useUrlState('q', '', codecs.str);
  const [type, setType] = useUrlState<string>('type', 'all', codecs.str);
  const debouncedSearch = useDebounced(search);
  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);
  const { gridProps, offset, limit } = useServerPagination(50, `${debouncedSearch}|${type}`);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewing, setViewing] = useState<Creative | null>(null);
  const [editing, setEditing] = useState<Creative | null>(null);
  const [editName, setEditName] = useState('');
  const [deleting, setDeleting] = useState<Creative | null>(null);

  const { data, isFetching, error } = useQuery({
    queryKey: ['creatives', offset, limit, debouncedSearch, type],
    queryFn: () => creativesService.list({
      offset, limit, search: debouncedSearch || undefined, type: type === 'all' ? undefined : (type as CreativeType),
    }),
    placeholderData: (prev) => prev,
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => creativesService.update(id, { name }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['creatives'] }); setEditing(null); },
    onError: failed('Rename failed'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => creativesService.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['creatives'] }); setDeleting(null); },
    onError: failed('Delete failed'),
  });

  const download = async (c: Creative) => {
    // Opened before the await: a window opened after it has lost the click and is blocked as a popup.
    const tab = window.open('', '_blank');
    try {
      const url = await creativesService.getDownloadUrl(c.id);
      if (tab) { tab.opener = null; tab.location.href = url; } else window.location.href = url;
    } catch (e) {
      tab?.close();
      failed('Download failed')(e);
    }
  };

  const columns: GridColDef<Creative>[] = [
    {
      field: 'preview', headerName: '', width: 72, sortable: false, disableColumnMenu: true,
      renderCell: (p: GridRenderCellParams<Creative>) => <Thumb creative={p.row} onOpen={() => setViewing(p.row)} />,
    },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 220 },
    {
      field: 'type', headerName: 'Type', width: 100,
      renderCell: (p: GridRenderCellParams<Creative>) => <Chip size="small" label={p.value} />,
    },
    {
      field: 'dimensions', headerName: 'Dimensions', width: 120, sortable: false,
      valueGetter: (_v, row) => (row.width && row.height ? `${row.width}×${row.height}` : '—'),
    },
    {
      field: 'duration_seconds', headerName: 'Duration', width: 90,
      valueFormatter: (v: number | null) => formatDuration(v),
    },
    {
      field: 'file_size', headerName: 'Size', width: 100,
      valueFormatter: (v: number) => formatFileSize(v),
    },
    {
      field: 'tags', headerName: 'Tags', width: 200, sortable: false,
      renderCell: (p: GridRenderCellParams<Creative>) => (
        <Box sx={{ display: 'flex', gap: 0.5, overflow: 'hidden' }}>
          {(p.row.tags ?? []).map((t) => <Chip key={t} size="small" variant="outlined" label={t} />)}
        </Box>
      ),
    },
    {
      field: 'source', headerName: 'Source', width: 100,
      valueFormatter: (v: Creative['source']) => SOURCE_LABEL[v] ?? v,
    },
    ...(workspaces.length > 1 ? [{
      field: 'workspace_id', headerName: 'Workspace', width: 150,
      valueFormatter: (v: string) => workspaceName.get(v) ?? v,
    } as GridColDef<Creative>] : []),
    {
      field: 'created_at', headerName: 'Created', width: 110,
      valueFormatter: (v: string) => new Date(v).toLocaleDateString(),
    },
    {
      field: 'actions', headerName: '', width: 110, sortable: false, disableColumnMenu: true,
      renderCell: (p: GridRenderCellParams<Creative>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <TooltipIconButton size="small" title="Rename" onClick={() => { setEditName(p.row.name); setEditing(p.row); }}>
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton size="small" title="Download" onClick={() => download(p.row)}>
            <Download sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton size="small" title="Delete" onClick={() => setDeleting(p.row)}>
            <Delete sx={{ fontSize: 16 }} />
          </TooltipIconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box ref={filterRef} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>Creative Library</Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>Failed to load creatives. Please try again.</Alert>}
      </Box>

      <DataGridPremium
        rows={data?.data ?? []}
        columns={columns}
        loading={isFetching}
        {...gridProps(data?.total)}
        rowHeight={60}
        // The API sorts newest first and filters only by search and type; column sorts and filters would act on this page alone.
        disableColumnSorting
        disableColumnFilter
        showToolbar
        slots={{ toolbar: EntityGridToolbar }}
        slotProps={{ toolbar: {
          search, onSearch: setSearch,
          filters: (
            <ToggleButtonGroup size="small" exclusive value={type} onChange={(_, v) => v && setType(v)} aria-label="Type">
              {TYPES.map((t) => <ToggleButton key={t} value={t} sx={{ textTransform: 'capitalize', py: 0.25 }}>{t}</ToggleButton>)}
            </ToggleButtonGroup>
          ),
          actions: [{ icon: CloudUpload, hint: 'Upload creative', onClick: () => setUploadOpen(true) }],
        } }}
        disableRowSelectionOnClick
        onRowDoubleClick={(params: GridRowParams<Creative>, event: React.MouseEvent) => {
          if ((event.target as HTMLElement).closest('[data-field="actions"]')) return;
          setViewing(params.row);
        }}
        sx={{ bgcolor: 'background.paper', borderRadius: 2, height: gridHeight, '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' } }}
      />

      <UploadCreativeDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <CreativeViewerDialog creative={viewing} onClose={() => setViewing(null)} onDownload={download} />

      <DraggableDialog open={!!editing} onClose={() => !rename.isPending && setEditing(null)} maxWidth="xs" fullWidth title="Rename Creative" titleId="rename-creative-title">
        <DialogContent>
          <TextField
            autoFocus label="Name" fullWidth size="small" sx={{ mt: 1 }} value={editName}
            onChange={(e) => setEditName(e.target.value)} disabled={rename.isPending}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={rename.isPending}>Cancel</Button>
          <Button
            variant="contained" disabled={!editName.trim() || rename.isPending}
            onClick={() => editing && rename.mutate({ id: editing.id, name: editName.trim() })}
          >
            Save
          </Button>
        </DialogActions>
      </DraggableDialog>

      <ConfirmDialog
        open={!!deleting}
        title="Delete creative"
        destructive
        body={deleting ? `Delete "${deleting.name}" from the library?` : ''}
        confirmLabel="Delete"
        busyLabel="Deleting..."
        busy={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        onClose={() => setDeleting(null)}
      />
    </Box>
  );
}
